"use client";
import { Link } from "@/components/CustomLink";
import { useSite } from "@/lib/hooks/site";
import { useHeaderTitle } from "@/hooks/useHeaderTitle";
import { useEffect, useState } from "react";
import { useVenueDetail } from "@/hooks/venueContext";
import { useTranslation } from "@/hooks/translationContext";
import { GLOBAL_VENUE_DISCOUNT_PERCENT_KEY } from "@/@core/constants/discount";
import { useVenueCompanyMaxDiscount } from "@/hooks/useVenueCompanyMaxDiscount";
import dynamic from "next/dynamic";
import S3Image from "@/components/S3Image";
import { DEFAULT_IMAGES } from "@/lib/constants";
import StarIcon from "@heroicons/react/24/solid/StarIcon";
import MapPinIcon from "@heroicons/react/24/solid/MapPinIcon";
import PaperAirplaneIcon from "@heroicons/react/24/solid/PaperAirplaneIcon";
import ChatBubbleLeftIcon from "@heroicons/react/24/outline/ChatBubbleLeftIcon";
import ClockIcon from "@heroicons/react/24/outline/ClockIcon";

import VenueCardCarousel from "@/components/views/list/card/VenueCardCarousel";
import { formatDistance } from "@/services/osrm.service";
import { useCurrentDeviceContext } from "@/context/DeviceContext";
import WhatsappLinkWithBeacon from "@/components/WhatsappLinkWithBeacon";
import useGetWpHref from "@/hooks/useGetWpHref";
import useWhatsappContext from "@/hooks/useWhatsappContext";
import { useContactWithVenuesLink } from "@/hooks/useContactWithVenuesLink";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";
import { isPhysiotherapistVenue } from "@/lib/venue-business-type";
import { usePlausible } from "next-plausible";
import { useSession } from "next-auth/react";
import { Button } from "@/components/ui/button";
import {
  CLICK_QUOTE_EVENT_SECTIONS,
  PLAUSIBLE_GOAL_KEYS,
} from "@/@core/constants/plausible";
import {
  WHATSAPP_SECTIONS,
  WHATSAPP_TAGS,
} from "@/@core/enums/whatsapp_tags.enum";

const VenueDetailContactModal = dynamic(
  () => import("@/components/views/venue-details/new-details/ContactModal"),
);

const CompanyDiscountsModal = dynamic(
  () => import("@/components/views/company-discounts/CompanyDiscountsModal"),
);

const CompanyOfferModal = dynamic(
  () => import("@/components/views/all-companies/modals/offer-modal"),
);

const CompanyActionCheckerModal = dynamic(
  () => import("@/components/views/modals/company/ActionChecker"),
);

interface Props extends Venue.List {
  firstPriority?: boolean;
  liked?: boolean;
  type?: "company" | "normal";
  companyDatas?: any;
  titleHeadingLevel: "h2" | "h3";
  distance?: number; // Distance in kilometers
  index?: number;
  serviceSlug?: string;
  /**
   * Caps how many photos the desktop card renders, overriding the per-site
   * `venueCard.numberOfImages` setting. The homepage passes 1 so its grid of
   * cards costs one <img> each instead of four (SAB-345).
   */
  photoLimit?: number;
}

export default function VenueCard(props: Props) {
  const { slug, title, description, address } = props?.descriptions?.[0] || {};
  const site = useSite();
  const isAskQuestionEnabled = useIsAskQuestionEnabled();
  const { title: linkTitle, isVenueListPage } = useHeaderTitle();
  const { setVenue } = useVenueDetail();
  const { t } = useTranslation();
  const { device } = useCurrentDeviceContext();
  const isMobile = device === "mobile";
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [companyOfferModalOpen, setCompanyOfferModalOpen] = useState(false);
  const { status: sessionStatus } = useSession();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get mobile thumbnail image (first photo, or fallback to logo)
  const getMobileImageSrc = () => {
    // Default to first photo
    if (props.photos?.[0]) {
      return `venue/${props.id}/photo/${props.photos[0].url}`;
    }
    // Fallback to venue logo
    return props.logo ? `venue/${props.id}/${props.logo}` : DEFAULT_IMAGES.VENUE_LOGO;
  };

  const [mobileImageSrc, setMobileImageSrc] = useState(getMobileImageSrc);

  useEffect(() => {
    setMobileImageSrc(getMobileImageSrc());
  }, [props.id, props.logo, props.photos, props.manager]);

  const getLinkTitle = () => {
    return `${props.descriptions?.[0]?.title}${isVenueListPage && linkTitle ? " - " + linkTitle : ""}`;
  };

  // Physiotherapist venues show no membership plan, discount, company
  // discount or quote CTA on their cards (SAB-362)
  const isPhysiotherapist = isPhysiotherapistVenue(props);

  // Get discount info
  const globalDiscountPercent = t(GLOBAL_VENUE_DISCOUNT_PERCENT_KEY);
  const maxDiscountPercent =
    props.discounts && props.discounts.length > 0
      ? Math.max(...props.discounts.map((discount) => discount.percent))
      : 0;

  const hasDiscount = () => {
    if (isPhysiotherapist) return false;
    const activeDiscounts = props.discounts?.find((d) => d?.percent == 0);
    if (activeDiscounts) return false;
    return (
      props.discounts?.length > 0 ||
      globalDiscountPercent !== GLOBAL_VENUE_DISCOUNT_PERCENT_KEY
    );
  };

  const hasCompanyDiscount =
    !isPhysiotherapist && props.type === "company" && props.companyDatas?.discount;

  const siteHasCompanyDiscounts = site?.setting?.disabledPages?.company !== 1;
  const isListingCard = props.type !== "company" && typeof props.index === "number";
  const showCompanyDiscountSlot =
    isListingCard &&
    !isPhysiotherapist &&
    siteHasCompanyDiscounts &&
    (props.index as number) % 2 === 1;
  const showQuoteSlot =
    isListingCard &&
    !isPhysiotherapist &&
    !showCompanyDiscountSlot &&
    isAskQuestionEnabled;

  // Company page cards show that company's own discount (the one on its discount card);
  // listing cards show the venue's best company discount
  const venueCompanyMaxDiscount = useVenueCompanyMaxDiscount(
    props.id,
    showCompanyDiscountSlot,
  );
  const companyDiscountPercent = hasCompanyDiscount
    ? props.companyDatas.discount
    : venueCompanyMaxDiscount;
  const companyDiscountLabel = companyDiscountPercent
    ? t("listpage-card-company-discount-badge", {
        discountPercent: companyDiscountPercent,
      })
    : t("listpage-card-company-discount-label");

  const quoteHref = useContactWithVenuesLink();
  const quoteTitle = t("quote-button-title");
  const plausible = usePlausible();
  const handleQuoteClick = () => {
    plausible(PLAUSIBLE_GOAL_KEYS.CLICK_QUOTE_EVENT, {
      props: {
        title,
        district: props.district?.title,
        section: CLICK_QUOTE_EVENT_SECTIONS.VENUE_CARD,
      },
    });
  };

  // Service-specific description shown only on the matching service listing page
  const activeServiceDescription = props.serviceSlug
    ? props.services?.find(
        (s) => s?.service?.descriptions?.[0]?.slug === props.serviceSlug,
      )?.description?.trim() || ""
    : "";

  // Working hours summary text (managed in the panel) shown with a clock icon
  const workingHoursText = props.hours?.description?.trim() || "";

  const handleContactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setVenue(props);
    setContactModalOpen(true);
  };

  // Listing cards open the venue's company list; picking a company there opens
  // its discount card
  const handleDiscountClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setVenue(props);
    setDiscountModalOpen(true);
  };

  // On a company discount page the company is already chosen, so open that
  // company's discount card directly (or the sign up / log in drawer when the
  // visitor isn't logged in) (SAB-372)
  const handleCompanyDiscountClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setVenue(props);
    setCompanyOfferModalOpen(true);
  };

  const getWhatsappHrefUrl = useGetWpHref();
  const whatsappContext = useWhatsappContext();
  const visitorDiscountPercent = !props.discounts?.length
    ? globalDiscountPercent
    : maxDiscountPercent;
  const visitorDiscountWhatsappHref = props.whatsappPhoneNumber
    ? getWhatsappHrefUrl({
        tag: WHATSAPP_TAGS.DISCOUNT,
        phone: props.whatsappPhoneNumber,
        translateValues: whatsappContext.getContext({
          venue: title,
          "discount-visitors": visitorDiscountPercent,
        }),
      })
    : "";

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const directionsHref =
    props.map_latitude && props.map_longitude
      ? `https://www.google.com/maps/dir/?api=1&destination=${props.map_latitude},${props.map_longitude}`
      : "";

  const TitleComponent = props.titleHeadingLevel === "h2" ? "h2" : "h3";

  return (
    <article className="relative bg-white border border-[#d9d9d9] md:border-[#ededed] rounded-[20px] md:rounded-[18px] p-4 md:p-0 w-full max-w-[402px] hover:shadow-lg md:shadow-[0px_1px_5.3px_0px_rgba(0,0,0,0.25)] md:hover:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.3)] transition-shadow duration-200 cursor-pointer md:overflow-hidden">
      {/* Contact Modal */}
      {contactModalOpen && (
        <VenueDetailContactModal
          addressLink={`https://www.google.com/maps/search/${props.map_latitude || 0}, ${props.map_longitude || 0}`}
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
        />
      )}

      {/* Company Discounts Modal */}
      {discountModalOpen && (
        <CompanyDiscountsModal
          id={props.id}
          isOpen={discountModalOpen}
          onClose={() => setDiscountModalOpen(false)}
        />
      )}

      {/* Company Discount Card (company discount pages) */}
      {companyOfferModalOpen &&
        (sessionStatus === "authenticated" ? (
          <CompanyOfferModal
            {...props.companyDatas}
            venue={{ id: props.id, title }}
            isOpen={companyOfferModalOpen}
            onClose={() => setCompanyOfferModalOpen(false)}
          />
        ) : (
          <CompanyActionCheckerModal
            isOpen={companyOfferModalOpen}
            onClose={() => setCompanyOfferModalOpen(false)}
          />
        ))}

      {/* ========== MOBILE LAYOUT ========== */}
      {isMobile && (
      <div>
        {/* Title Row - full width on top */}
        <div className="flex items-center gap-1.5 mb-3">
          <TitleComponent
            className="flex-1 min-w-0 font-semibold text-[16px] text-black tracking-[-0.16px] truncate"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {title}
          </TitleComponent>
        </div>

        {/* Main Content */}
        <div className="flex gap-3">
          {/* Left: Thumbnail Image — square, width matches the %10 İndirim button below */}
          <div className="flex-shrink-0 w-[95px] h-[95px] rounded-[10px] overflow-hidden">
            <S3Image
              width={95}
              height={95}
              src={mobileImageSrc}
              alt={title || ""}
              title={title || ""}
              onError={() => setMobileImageSrc(DEFAULT_IMAGES.VENUE_LOGO)}
              className="w-full h-full object-cover"
              loading={props.firstPriority ? "eager" : "lazy"}
            />
          </div>

          {/* Right: Info Section */}
          <div className="flex-1 min-w-0 flex flex-col h-[95px]">
            {/* Badges Row - aligned with top of photo */}
            <div className="flex items-center gap-2 flex-nowrap min-w-0">
              {/* Plan Badge */}
              {!isPhysiotherapist && (
                <div className="flex items-center gap-1.5 bg-[#f4f4f4] rounded-[10.87px] px-2 py-0.5 shrink-0">
                  <StarIcon className="size-2.5 text-[#9f9f9f]" />
                  <span
                    className="text-[#9f9f9f] text-[11px] font-medium tracking-[-0.11px] truncate"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {props.plan?.descriptions?.[0]?.title}
                  </span>
                </div>
              )}

              {/* Location Badge */}
              <div className="flex items-center gap-1.5 bg-[#f4f4f4] rounded-[10.87px] px-2 py-0.5 min-w-0">
                <MapPinIcon className="size-2.5 text-[#9f9f9f] shrink-0" />
                <span
                  className="text-[#9f9f9f] text-[11px] font-medium tracking-[-0.11px] truncate"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {props.district?.title}, {props.city?.title}
                </span>
              </div>
            </div>

            {/* Distance Row - directly below plan badge; nav icon at far right */}
            {props.distance !== undefined && (
              <div className="mt-1.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5 pl-2">
                  <MapPinIcon className="size-3 text-[#ef4444]" />
                  <span
                    className="text-[#ef4444] text-[13px] font-medium tracking-[-0.13px]"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {formatDistance(props.distance)}
                  </span>
                </div>
                {directionsHref && (
                  <a
                    href={directionsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={stopPropagation}
                    aria-label={t("listpage-card-directions-label")}
                    className="z-20 inline-flex items-center justify-center size-7 rounded-full bg-[#f4f4f4] text-[#525252] hover:bg-[#e5e5e5] transition-colors"
                  >
                    <PaperAirplaneIcon className="size-3.5 -rotate-45" />
                  </a>
                )}
              </div>
            )}

            {/* Address - 2 lines max; 2nd line aligns with photo bottom */}
            {address && (
              <p
                className="mt-auto text-[12px] text-[#6f6f6f] tracking-[-0.12px] line-clamp-2 leading-[1.35]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {address}
              </p>
            )}
          </div>
        </div>

        {/* Working Hours - shown right below the photo on mobile */}
        {workingHoursText && (
          <div className="flex items-start gap-1.5 mt-3">
            <ClockIcon
              aria-label={t("listpage-card-working-hours-label")}
              className="size-3.5 text-[#737373] flex-shrink-0 mt-0.5"
            />
            <p
              className="text-[12px] text-[#6f6f6f] tracking-[-0.12px] line-clamp-2 leading-[1.35]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {workingHoursText}
            </p>
          </div>
        )}

        {/* Service Description (only on matching service listing page) */}
        {activeServiceDescription && (
          <div className="flex items-start gap-1.5 mt-3">
            <ChatBubbleLeftIcon
              aria-label={t("listpage-card-description-label")}
              className="size-3.5 text-[#737373] flex-shrink-0 mt-0.5"
            />
            <p
              className="text-[12px] text-[#6f6f6f] tracking-[-0.12px] line-clamp-2 leading-[1.35]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {activeServiceDescription}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 mt-4">
          {/* Discount Button */}
          {hasDiscount() && props.whatsappPhoneNumber ? (
            <div
              onClick={stopPropagation}
              className="z-20 shrink-0 whitespace-nowrap"
            >
              <WhatsappLinkWithBeacon
                section={WHATSAPP_SECTIONS.DISCOUNT}
                venueId={props.id}
                title={t("listpage-card-ask-discount-button", {
                  discountPercent: visitorDiscountPercent,
                })}
                href={visitorDiscountWhatsappHref}
                className="inline-flex items-center justify-center w-[95px] h-[34px] px-1.5 bg-white border border-[#e5e5e5] rounded-lg text-[12px] font-medium text-[#525252] hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <span
                  className="whitespace-nowrap"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  %{visitorDiscountPercent} İndirim
                </span>
              </WhatsappLinkWithBeacon>
            </div>
          ) : isListingCard ? (
            <div
              aria-hidden="true"
              className="invisible pointer-events-none shrink-0 whitespace-nowrap"
            >
              <span
                className="inline-flex items-center justify-center w-[95px] h-[34px] px-1.5 border border-transparent rounded-lg text-[12px] font-medium whitespace-nowrap"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                %10 İndirim
              </span>
            </div>
          ) : null}

          {/* Company Discount Button */}
          {hasCompanyDiscount && (
            <button
              onClick={handleCompanyDiscountClick}
              className="h-[34px] px-3 bg-gradient hover:bg-gradient-secondary rounded-lg text-[13px] font-bold text-white transition-colors z-20 whitespace-nowrap shrink-0"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t("listpage-card-company-discount-badge", {
                discountPercent: companyDiscountPercent,
              })}
            </button>
          )}

          {/* Listing Middle Button: alternates between Quote / Company Discount */}
          {isListingCard && (
            <div className="flex-1 min-w-0 flex justify-center z-20">
              {showCompanyDiscountSlot && (
                <button
                  onClick={handleDiscountClick}
                  className="h-[34px] px-3 bg-gradient hover:bg-gradient-secondary rounded-lg text-[13px] font-bold text-white transition-colors whitespace-nowrap"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {companyDiscountLabel}
                </button>
              )}
              {showQuoteSlot && (
                <Link
                  href={quoteHref}
                  onClick={handleQuoteClick}
                  prefetch={false}
                  title={quoteTitle}
                  aria-label={quoteTitle}
                >
                  <Button
                    aria-label={quoteTitle}
                    variant="gradient"
                    className="!h-[34px] md:!h-[36px] flex items-center font-bold text-[13px] justify-center gap-2 px-2.5 md:px-4 rounded-lg md:rounded-[10px] whitespace-nowrap"
                  >
                    {quoteTitle}
                  </Button>
                </Link>
              )}
            </div>
          )}

          {/* Contact Button */}
          <button
            onClick={handleContactClick}
            className={`h-[34px] px-4 bg-[#20b038] rounded-lg text-[13px] font-semibold text-white z-20 hover:bg-[#1a9930] transition-colors shrink-0 ${isListingCard ? "" : "ml-auto"}`}
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            İletişim
          </button>
        </div>
      </div>
      )}

      {/* ========== DESKTOP LAYOUT ========== */}
      {!isMobile && (
      <div className="p-3">
        {/* Photo Carousel with rounded corners */}
        <div className="rounded-[12px] overflow-hidden">
          <VenueCardCarousel
            id={props.id}
            photos={props.photos || []}
            descriptions={props.descriptions}
            firstPriority={props.firstPriority}
            setCurrentIndex={setCurrentIndex}
            currentIndex={currentIndex}
            slug={slug || ""}
            districtSlug={props.district?.slug || ""}
            photoLimit={props.photoLimit}
          />
        </div>

        {/* Content Section */}
        <div className="pt-3 px-1">
          {/* Title Row */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <TitleComponent
              className="font-semibold text-[17px] text-black truncate"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {title}
            </TitleComponent>
          </div>

          {/* Badges Row */}
          <div className="flex items-center gap-1.5 flex-nowrap min-w-0 mb-2">
            {/* Plan Badge */}
            {!isPhysiotherapist && (
              <div className="flex items-center gap-1 bg-[#f5f5f5] rounded-full px-2 py-0.5 shrink-0">
                <StarIcon className="size-2.5 text-[#a3a3a3]" />
                <span
                  className="text-[#a3a3a3] text-[11px] font-medium truncate"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {props.plan?.descriptions?.[0]?.title}
                </span>
              </div>
            )}

            {/* Location Badge */}
            <div className="flex items-center gap-1 text-[#a3a3a3] min-w-0">
              <MapPinIcon className="size-2.5 shrink-0" />
              <span
                className="text-[11px] font-medium truncate"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {props.district?.title}, {props.city?.title}
              </span>
            </div>
          </div>

          {/* Distance Row - aligned with photo bottom edge */}
          {props.distance !== undefined && (
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1 text-[#ef4444]">
                <MapPinIcon className="size-3" />
                <span
                  className="text-[12px] font-medium"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {formatDistance(props.distance)}
                </span>
              </div>
              {directionsHref && (
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={stopPropagation}
                  aria-label={t("listpage-card-directions-label")}
                  className="z-20 inline-flex items-center justify-center size-7 rounded-full bg-[#f5f5f5] text-[#525252] hover:bg-[#e5e5e5] transition-colors"
                >
                  <PaperAirplaneIcon className="size-3.5 -rotate-45" />
                </a>
              )}
            </div>
          )}

          {/* Address - 2 lines max with ellipsis */}
          {address && (
            <p
              className="mb-3 text-[12px] text-[#6f6f6f] tracking-[-0.12px] line-clamp-2 leading-[1.35]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {address}
            </p>
          )}

          {/* Working Hours - shown right below the address on desktop */}
          {workingHoursText && (
            <div className="flex items-start gap-1.5 mb-3">
              <ClockIcon
                aria-label={t("listpage-card-working-hours-label")}
                className="size-3.5 text-[#737373] flex-shrink-0 mt-0.5"
              />
              <p
                className="text-[12px] text-[#6f6f6f] tracking-[-0.12px] line-clamp-2 leading-[1.35]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {workingHoursText}
              </p>
            </div>
          )}

          {/* Service Description (only on matching service listing page) */}
          {activeServiceDescription && (
            <div className="flex items-start gap-1.5 mb-3">
              <ChatBubbleLeftIcon
                aria-label={t("listpage-card-description-label")}
                className="size-3.5 text-[#737373] flex-shrink-0 mt-0.5"
              />
              <p
                className="text-[12px] text-[#6f6f6f] tracking-[-0.12px] line-clamp-2 leading-[1.35]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {activeServiceDescription}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Discount Button */}
            {hasDiscount() && props.whatsappPhoneNumber ? (
              <div onClick={stopPropagation} className="z-20">
                <WhatsappLinkWithBeacon
                  section={WHATSAPP_SECTIONS.DISCOUNT}
                  venueId={props.id}
                  title={t("listpage-card-ask-discount-button", {
                  discountPercent: visitorDiscountPercent,
                })}
                  href={visitorDiscountWhatsappHref}
                  className="inline-flex items-center h-[36px] px-3.5 bg-white border border-[#e5e5e5] rounded-[10px] text-[13px] font-medium text-[#525252] hover:bg-gray-50 transition-colors"
                >
                  <span style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    %{visitorDiscountPercent} İndirim
                  </span>
                </WhatsappLinkWithBeacon>
              </div>
            ) : isListingCard ? (
              <div aria-hidden="true" className="invisible pointer-events-none">
                <span
                  className="inline-flex items-center h-[36px] px-3.5 border border-transparent rounded-[10px] text-[13px] font-medium"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  %10 İndirim
                </span>
              </div>
            ) : null}

            {/* Company Discount Button */}
            {hasCompanyDiscount && (
              <button
                onClick={handleCompanyDiscountClick}
                className="h-[36px] px-3.5 bg-gradient hover:bg-gradient-secondary rounded-[10px] text-[13px] font-bold text-white transition-colors z-20"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t("listpage-card-company-discount-badge", {
                discountPercent: companyDiscountPercent,
              })}
              </button>
            )}

            {/* Listing Middle Button: alternates between Quote / Company Discount */}
            {isListingCard && (
              <div className="flex-1 flex justify-center z-20">
                {showCompanyDiscountSlot && (
                  <button
                    onClick={handleDiscountClick}
                    className="h-[36px] px-3.5 bg-gradient hover:bg-gradient-secondary rounded-[10px] text-[13px] font-bold text-white transition-colors"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {companyDiscountLabel}
                  </button>
                )}
                {showQuoteSlot && (
                  <Link
                    href={quoteHref}
                    onClick={handleQuoteClick}
                    prefetch={false}
                    title={quoteTitle}
                    aria-label={quoteTitle}
                  >
                    <Button
                      aria-label={quoteTitle}
                      variant="gradient"
                      className="!h-[34px] md:!h-[36px] flex items-center font-bold text-[13px] justify-center gap-2 px-4 rounded-lg md:rounded-[10px]"
                    >
                      {quoteTitle}
                    </Button>
                  </Link>
                )}
              </div>
            )}

            {/* Contact Button */}
            <button
              onClick={handleContactClick}
              className={`h-[36px] px-5 bg-[#20b038] rounded-[10px] text-[13px] font-semibold text-white z-20 hover:bg-[#1a9930] transition-colors ${isListingCard ? "" : "ml-auto"}`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              İletişim
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Full Card Link Overlay */}
      <Link
        title={getLinkTitle()}
        aria-label={getLinkTitle()}
        href={`/${site.setting?.routes?.venueDetail}/${slug}-${props.district?.slug}`}
        scroll={true}
        className="absolute inset-0 z-10"
      />
    </article>
  );
}
